import {
  IconDefinition,
  faArrowDown,
  faPause,
  faPlay,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { format } from 'date-fns';
import { LibraryVideo, VideoType } from '../videos.domain';
import { downloadURI, secondsToHHMMSS } from '../videos.helpers';
import { UploadState } from '../videos.hooks';
import ReactPlayerComponent, { ReactPlayerProps } from 'react-player';
import { PrimaryButton } from '@client/new/components/button';
import { colors } from '@client/new/components/colors';
import { VideoMenu } from './VideoMenu';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

const ReactPlayer =
  ReactPlayerComponent as unknown as React.FC<ReactPlayerProps>;

export type CustomPlayerProps = {
  src: string;
  poster?: string;
  isMuted?: boolean;
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
};
export function CustomPlayer({
  src,
  poster,
  isMuted,
  className,
  style,
  width,
  height,
}: CustomPlayerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const togglePlay = () => setIsPlaying((prev) => !prev);
  const openAndPlay = () => {
    setIsOpen(true);
    setIsPlaying(true);
  };

  if (!isOpen) {
    return (
      <div onClick={openAndPlay} className="relative h-full flex">
        <img
          src={poster}
          width={width}
          height={height}
          loading="lazy"
          style={{ maxHeight: height, maxWidth: width, objectFit: 'contain' }}
        />
        <button className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-full bg-gray-400 bg-opacity-50 w-[40px] h-[40px]">
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} color="white" />
        </button>
      </div>
    );
  }

  return (
    <div className={className + ' relative'} onClick={togglePlay}>
      <ReactPlayer
        playing={isPlaying}
        playsinline
        width={width}
        height={height}
        url={src}
        config={{ file: { attributes: { poster } } }}
        muted={isMuted}
        playIcon={<FontAwesomeIcon icon={faPlay} />}
        onEnded={() => setIsPlaying(false)}
        style={{
          borderRadius: '6px',
          overflow: 'hidden',
          ...style,
        }}
      />
      {!isPlaying ? (
        <button className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-full bg-gray-400 bg-opacity-50 w-[40px] h-[40px]">
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} color="white" />
        </button>
      ) : null}
    </div>
  );
}

export const ClipInProgress = (
  p: Pick<UploadState, 'fileName' | 'progress'>,
) => (
  <div className="bg-white p-2 pb-4 rounded-md h-full w-full flex flex-col">
    <div
      className="flex-1"
      style={{
        background: colors.secondary400,
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${p.progress}%`,
          background: colors.primaryMain900,
        }}
      />
      <Typography
        variant="h4"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
      >
        {p.progress}%
      </Typography>
    </div>

    <div className="text-gray-500 flex justify-between text-sm mt-2">
      Uploading...
    </div>
    <Typography
      variant="body1"
      className="overflow-hidden text-ellipsis whitespace-nowrap mt-2"
    >
      {p.fileName}
    </Typography>
  </div>
);

const ClipsGalleryItem = (p: {
  video: LibraryVideo;
  displayActions?: boolean;
}) => {
  const isMux = p.video.videoSrc.includes('mux.com');
  const displayActions = p.displayActions ?? true;
  const display = {
    actions: displayActions,
    download: true,
    mute: !isMux,
  };

  return (
    <div className="bg-white p-2 pb-4 rounded-md flex flex-col gap-2 h-full">
      <div className="rounded-md flex-1 overflow-hidden bg-black relative">
        <CustomPlayer
          src={p.video.videoSrc}
          poster={p.video.poster}
          height="100%"
          width="100%"
          className="h-full w-full"
        />

        {when(
          display.actions,
          <div className="absolute flex justify-between w-full top-2 left-0 px-2">
            <div className="flex gap-4">
              {when(
                display.download,
                <VideoActionIcon
                  icon={faArrowDown}
                  title="Download"
                  onClick={() => downloadURI(p.video.downloadUrl, p.video.name)}
                />,
              )}
            </div>

            {when(
              !isMux,
              <VideoMenu
                video={p.video}
                display={{
                  changeName: !isMux,
                  changeThumbnail: !isMux,
                  createExercise: true,
                }}
              />,
            )}
          </div>,
        )}
      </div>

      <div className="text-gray-500 flex justify-between text-sm line-clamp-1">
        <span>{format(new Date(p.video.createdAt), 'MM/dd/yyyy')}</span>
        <span>{secondsToHHMMSS(p.video.duration)}</span>
      </div>
      <Typography
        variant="body1"
        className="overflow-hidden text-ellipsis whitespace-nowrap line-clamp-1"
      >
        {p.video.name}
      </Typography>
    </div>
  );
};

function VideoActionIcon({
  icon,
  title,
  onClick,
}: {
  icon: IconDefinition;
  title: string;
  onClick: () => void;
}) {
  return (
    <Tooltip title={title}>
      <div
        className="bg-gray-400 bg-opacity-50 cursor-pointer w-[34px] h-[20px] flex items-center justify-center rounded-md"
        onClick={onClick}
      >
        <FontAwesomeIcon icon={icon} color="white" />
      </div>
    </Tooltip>
  );
}

type ClipsGalleryProps = {
  list: Array<LibraryVideo>;
  inProgressList?: Array<Omit<UploadState, 'asyncState'>>;
  videoType: VideoType;
  shortMaxWidth?: number;
  onClick?: (v: LibraryVideo) => void;
  selectedClip?: LibraryVideo;
};

export function ClipsGallery(p: ClipsGalleryProps) {
  const [limit, setLimit] = useState(100);

  const handleShowMore = () => {
    setLimit((prev) => prev + 100);
  };

  const displayShowMore = p.list.length > limit;

  return (
    <div className="flex flex-col gap-4">
      <div>
        Displaying {Math.min(limit, p.list.length)}/{p.list.length} videos
      </div>

      <div
        className="grid gap-4 w-full"
        style={
          p.videoType === 'short'
            ? {
                gridTemplateColumns: `repeat(auto-fill, minmax(${
                  p.shortMaxWidth || 210
                }px, 1fr))`,
              }
            : {
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gridAutoRows: 355,
              }
        }
      >
        {p.inProgressList?.map((v) => (
          <div
            key={v.fileName}
            style={
              p.videoType === 'short' ? { aspectRatio: '8/16' } : undefined
            }
          >
            <ClipInProgress
              key={v.fileName}
              progress={v.progress}
              fileName={v.fileName}
            />
          </div>
        ))}

        {p.list.slice(0, limit).map((video, index) => {
          const isSelected = p.selectedClip?.id === video.id;
          return (
            <div
              key={index}
              className="shadow-card relative w-full rounded-lg border-solid border-2"
              style={{
                borderColor: isSelected ? colors.primaryMain900 : 'white',
                cursor: p.onClick ? 'pointer' : undefined,
              }}
              onClick={(e) => {
                e.preventDefault();
                p.onClick && p.onClick(video);
              }}
            >
              {p.onClick && !isSelected ? (
                <div className="absolute w-full h-full z-50 opacity-0" />
              ) : null}
              <ClipsGalleryItem video={video} displayActions={!p.onClick} />
              {isSelected && (
                <FontAwesomeIcon
                  className="absolute top-4 left-4 bg-white rounded-full w-8 h-8"
                  icon={faCircleCheck}
                  color={colors.primaryMain900}
                />
              )}
            </div>
          );
        })}
      </div>

      {displayShowMore ? (
        <div className="mt-8">
          <PrimaryButton onClick={handleShowMore}>Show More</PrimaryButton>
        </div>
      ) : null}
    </div>
  );
}

function when(x: boolean, jsx: JSX.Element) {
  return x ? jsx : null;
}
